import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LayoutChangeEvent, Modal, Platform, Animated as RNAnimated } from 'react-native';

import { IconClose, Pressable, Text, XStack, YStack } from '@fhs/ui';
import { useBoxRef } from '@fhs/utils';
import { ToastProvider } from '@fhs-legacy/native-base';

import type { ActionSheetProps } from '../action-sheet/types';

import { ActionSheetScrollView } from './action-sheet-scroll-view';
import { styles } from './bottom-drawer-styles';
import {
  getDialogAccessibilityLabel,
  getModalCloseAccessibilityLabel,
} from './dialog-accessibility';
import { useFocusWebAriaModalOnOpen } from './use-focus-web-aria-modal-on-open';
import { useSuppressWebRootAccessibility } from './use-suppress-web-root-accessibility';

const NoScrollableDrawer = ({ children, ...props }: PropsWithChildren) => (
  <YStack style={[styles.drawer]} {...props}>
    {children}
  </YStack>
);

export const BottomDrawer = ({
  isVisible,
  onClose,
  headerTitle,
  sheetAccessibilityLabel,
  hideHeader,
  sheetAriaDescribedBy,
  children,
  onRequestClose,
  containerStyle,
  drawerProps: { onLayout: onLayoutProp, ...drawerProps } = {},
  footer,
  isScrollableDrawer,
}: ActionSheetProps) => {
  const dialogA11yLabel = useMemo(
    () => getDialogAccessibilityLabel(sheetAccessibilityLabel, headerTitle),
    [sheetAccessibilityLabel, headerTitle]
  );
  const closeA11yLabel = useMemo(
    () => getModalCloseAccessibilityLabel(sheetAccessibilityLabel, headerTitle),
    [sheetAccessibilityLabel, headerTitle]
  );

  const dialogLabel = headerTitle?.trim() ?? '';
  const showHeaderRow = Boolean(dialogLabel) && !hideHeader;
  const shouldFocusWebDialog =
    Boolean(isVisible) &&
    (Boolean(dialogLabel) || Boolean(sheetAccessibilityLabel?.trim()));

  useSuppressWebRootAccessibility(Boolean(isVisible));
  useFocusWebAriaModalOnOpen(shouldFocusWebDialog);

  const [drawerHeight, setDrawerHeight] = useState(0);
  const handleRequestCloseRef = useBoxRef(() => {
    onClose();
  });
  const animation = useRef(new RNAnimated.Value(0));

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    setDrawerHeight(height);
  }, []);

  useEffect(() => {
    if (isVisible) {
      RNAnimated.timing(animation.current, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start();
      return;
    }

    RNAnimated.timing(animation.current, {
      toValue: 0,
      duration: 300,
      useNativeDriver: true,
    }).start(() => handleRequestCloseRef.current?.());
  }, [isVisible, handleRequestCloseRef]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !isVisible) {
      return;
    }
    const dismiss = () => {
      (onRequestClose ?? handleRequestCloseRef.current)?.();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dismiss();
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }
  }, [isVisible, onRequestClose, handleRequestCloseRef]);

  const translateY = animation.current.interpolate({
    inputRange: [0, 1],
    outputRange: [drawerHeight, 0],
  });

  const opacity = animation.current.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 0.75],
  });

  const Drawer = isScrollableDrawer ? ActionSheetScrollView : NoScrollableDrawer;

  return (
    <Modal
      visible={isVisible}
      transparent
      style={styles.overlay}
      onRequestClose={onRequestClose ?? handleRequestCloseRef.current}
    >
      <ToastProvider noOverlayContainer>
        <RNAnimated.View
          style={[styles.fullScreen, { opacity }]}
          // Backdrop: no tab stop, hidden from the accessibility tree (avoids "close dropdown" + page bleed-through).
          {...(Platform.OS === 'web'
            ? ({ accessibilityElementsHidden: true, importantForAccessibility: 'no-hide-descendants' } as const)
            : {})}
        >
          <Pressable
            accessible={false}
            focusable={false}
            tabIndex={-1}
            // @ts-expect-error RN web — keep backdrop out of SR and tab order
            aria-hidden
            style={styles.fullScreen}
            onPress={handleRequestCloseRef.current}
          />
        </RNAnimated.View>
        <RNAnimated.View
          accessibilityLabel={dialogA11yLabel}
          accessibilityViewIsModal
          {...({
            'data-fhs-action-sheet-dialog': 'true',
            tabIndex: -1,
            // @ts-expect-error RN web maps role to ARIA role
            role: 'dialog',
            'aria-label': dialogA11yLabel,
            ...(sheetAriaDescribedBy?.trim()
              ? { 'aria-describedby': sheetAriaDescribedBy.trim() }
              : {}),
            'aria-modal': true,
          } as Record<string, unknown>)}
          style={[
            styles.drawerContainer,
            { transform: [{ translateY }] },
            containerStyle,
            { maxHeight: '90%' },
          ]}
        >
          {showHeaderRow ? (
            <XStack accessible={false} style={styles.header}>
              <Text accessible={false} style={styles.headerTitle} weight="semibold">
                {dialogLabel}
              </Text>
              <Pressable
                accessibilityHint="Closes this sheet"
                accessibilityLabel={closeA11yLabel}
                accessibilityRole="button"
                style={styles.closeIcon}
                onPress={onClose}
                {...(Platform.OS === 'web'
                  ? ({
                      'data-fhs-action-sheet-dismiss': 'true',
                      'aria-label': closeA11yLabel,
                    } as Record<string, unknown>)
                  : {})}
              >
                <IconClose
                  size={24}
                  accessibilityElementsHidden
                  importantForAccessibility="no-hide-descendants"
                />
              </Pressable>
            </XStack>
          ) : null}
          <Drawer
            {...drawerProps}
            onLayout={ev => {
              onLayout(ev);
              onLayoutProp?.(ev);
            }}
          >
            {children}
          </Drawer>
          {footer}
        </RNAnimated.View>
      </ToastProvider>
    </Modal>
  );
};
